import { useEffect } from 'react'
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  Loader2,
  Pencil,
  Calendar,
  User,
  Building2,
  DoorOpen,
  FileText,
  Camera,
  CheckCircle2,
  AlertCircle,
  MapPin,
} from 'lucide-react'
import {
  useReport,
  useBuilding,
  useRoom,
  useEditWindow,
  EditWindowBadge,
  useReportImages,
  imageQueryKeys,
} from '@/hooks'
import { PageHeader, ChecklistEditor, NotFound, ImageGallery, HistoryTimeline, GPSMap } from '@/components'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useAuth } from '@/context/AuthContext'
import type { Image } from '@/types'

/**
 * Render the Photos card title based on image statuses. When all images are
 * confirmed we show a simple count; while some are still processing or failed
 * we surface that so the user knows the upload pipeline is still working.
 */
function renderPhotosTitle(images: Image[]): string {
  const confirmed = images.filter((img) => img.status === 'confirmed').length
  const pending = images.filter((img) => img.status === 'pending').length
  const failed = images.filter((img) => img.status === 'failed').length

  if (pending === 0 && failed === 0) {
    return `Photos (${confirmed})`
  }
  const parts: string[] = []
  if (confirmed > 0) parts.push(`${confirmed} ready`)
  if (pending > 0) parts.push(`${pending} processing`)
  if (failed > 0) parts.push(`${failed} failed`)
  return `Photos (${parts.join(', ')})`
}

export function ReportDetailPage() {
  const { reportId } = useParams<{ reportId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()

  // Seed the images cache from navigation state so the Photos card renders
  // immediately when arriving from the create flow — before the first fetch
  // returns. Runs once per mount; only fills the cache if it's empty.
  useEffect(() => {
    if (!reportId) return
    const optimistic = (location.state as { optimisticImages?: Image[] } | null)
      ?.optimisticImages
    if (!optimistic || optimistic.length === 0) return
    const key = imageQueryKeys.byReport(reportId)
    const existing = queryClient.getQueryData<Image[]>(key)
    if (!existing || existing.length === 0) {
      queryClient.setQueryData<Image[]>(key, optimistic)
    }
    // Revoke any object URLs the create page handed us when we leave this page.
    return () => {
      for (const img of optimistic) {
        if (img.download_url?.startsWith('blob:')) {
          URL.revokeObjectURL(img.download_url)
        }
      }
    }
  }, [reportId, location.state, queryClient])

  const { user } = useAuth()
  const { data: report, isLoading, error } = useReport(reportId ?? '')
  const { data: building } = useBuilding(report?.building_id ?? '')
  const { data: room } = useRoom(report?.room_id ?? '')
  const { data: images = [] } = useReportImages(reportId ?? '')
  const editWindowStatus = useEditWindow(report)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error || !report) {
    return <NotFound onBack={() => navigate('/reports')} />
  }

  const checkedCount = report.checklist.filter((item) => item.checked).length
  const totalCount = report.checklist.length
  const completionPercent =
    totalCount > 0 ? Math.round((checkedCount / totalCount) * 100) : 0

  return (
    <div className="space-y-6 print:space-y-4">
      <div className="flex items-center gap-4 print:hidden">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/reports">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <PageHeader
          title="Report Details"
          description={`Report from ${new Date(report.created_at).toLocaleDateString()}`}
          actions={
            <div className="flex items-center gap-3">
              <EditWindowBadge status={editWindowStatus} />
              {editWindowStatus.canEdit && (
                <Button asChild>
                  <Link to={`/reports/${reportId}/edit`}>
                    <Pencil className="mr-2 h-4 w-4" />
                    Edit Report
                  </Link>
                </Button>
              )}
            </div>
          }
        />
      </div>

      {/* Print Header */}
      <div className="hidden print:block">
        <h1 className="text-2xl font-bold">Cleaning Report</h1>
        <p className="text-muted-foreground">
          {new Date(report.created_at).toLocaleDateString()} at{' '}
          {new Date(report.created_at).toLocaleTimeString()}
        </p>
      </div>

      {/* Report Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Report Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex items-start gap-3">
              <Building2 className="h-5 w-5 mt-0.5 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Building
                </p>
                <p className="font-medium">{report.building_name ?? building?.name ?? '—'}</p>
                {building?.address && (
                  <p className="text-sm text-muted-foreground">
                    {building.address}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-start gap-3">
              <DoorOpen className="h-5 w-5 mt-0.5 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Room
                </p>
                <p className="font-medium">{report.room_name ?? room?.name ?? '—'}</p>
                {room?.floor && (
                  <p className="text-sm text-muted-foreground">
                    Floor {room.floor}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-start gap-3">
              <User className="h-5 w-5 mt-0.5 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Submitted by
                </p>
                <p className="font-medium">{report.employee_name || report.employee_id}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Calendar className="h-5 w-5 mt-0.5 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Submitted
                </p>
                <p className="font-medium">
                  {new Date(report.created_at).toLocaleDateString()}
                </p>
                <p className="text-sm text-muted-foreground">
                  {new Date(report.created_at).toLocaleTimeString()}
                </p>
              </div>
            </div>
          </div>
          <Separator />
          <div className="flex items-center gap-4">
            <Badge
              variant={completionPercent === 100 ? 'default' : 'secondary'}
            >
              {checkedCount} / {totalCount} items checked
            </Badge>
            <span className="text-sm text-muted-foreground">
              {completionPercent}% complete
            </span>
            {report.version > 1 && (
              <Badge variant="outline">v{report.version}</Badge>
            )}
            {/* GPS status — visible to admins and super admins only */}
            {(user?.role === 'admin' || user?.role === 'super_admin') && report.gps_status && (
              <>
                {report.gps_status === 'verified' && (
                  <Badge variant="outline" className="text-green-600 border-green-300">
                    <CheckCircle2 className="mr-1 h-3 w-3" />
                    GPS Verified
                  </Badge>
                )}
                {report.gps_status === 'outside_range' && (
                  <Badge variant="outline" className="text-amber-600 border-amber-300">
                    <AlertCircle className="mr-1 h-3 w-3" />
                    Outside Building Range
                  </Badge>
                )}
                {report.gps_status === 'unavailable' && (
                  <Badge variant="outline" className="text-amber-600 border-amber-300">
                    <MapPin className="mr-1 h-3 w-3" />
                    GPS Unavailable at Submission
                  </Badge>
                )}
                {report.gps_status === 'not_required' && (
                  <Badge variant="outline" className="text-muted-foreground">
                    Admin Submission
                  </Badge>
                )}
              </>
            )}
            {(user?.role === 'admin' || user?.role === 'super_admin') && !report.gps_status && (
              <Badge variant="outline" className="text-muted-foreground">
                Building Not Geocoded
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Checklist */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Checklist</CardTitle>
        </CardHeader>
        <CardContent>
          <ChecklistEditor items={report.checklist} onChange={() => {}} readOnly />
        </CardContent>
      </Card>

      {/* Notes */}
      {report.notes && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="whitespace-pre-wrap">{report.notes}</p>
          </CardContent>
        </Card>
      )}

      {/* GPS Location */}
      {building?.latitude != null && building?.longitude != null && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              GPS Location
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {report.gps_status === 'unavailable' && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  GPS was not available when this report was submitted.
                </AlertDescription>
              </Alert>
            )}
            {report.gps_status === 'not_required' && (
              <Alert>
                <AlertDescription>
                  This report was submitted by an admin — GPS location is not required.
                </AlertDescription>
              </Alert>
            )}
            <GPSMap
              geocodedLat={building.latitude}
              geocodedLng={building.longitude}
              centerLat={building.gps_center_latitude}
              centerLng={building.gps_center_longitude}
              radiusMetres={building.gps_radius_metres ?? 150}
              reportLat={report.gps_latitude}
              reportLng={report.gps_longitude}
              reportGPSStatus={report.gps_status}
            />
            <p className="text-xs text-muted-foreground">
              <span className="inline-block w-3 h-3 rounded-full bg-blue-500 mr-1 align-middle" />
              Validation zone
              {report.gps_latitude != null && (
                <>
                  {' · '}
                  <span className={`inline-block w-3 h-3 rounded-full mr-1 align-middle ${
                    report.gps_status === 'outside_range' ? 'bg-amber-500' : 'bg-green-500'
                  }`} />
                  Submission location
                </>
              )}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Images */}
      {images.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Camera className="h-5 w-5" />
              {renderPhotosTitle(images)}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ImageGallery images={images} />
          </CardContent>
        </Card>
      )}

      {/* History Timeline */}
      <HistoryTimeline
        entityType="reports"
        entityId={reportId ?? ''}
        createdAt={report.created_at}
        updatedAt={report.updated_at}
        updatedBy={report.updated_by}
        updatedByName={report.updated_by_name}
      />
    </div>
  )
}
